import { AREA_DEFINITIONS, getArea, isAreaId, STARTING_AREA } from '@/data/maps';
import { GameClock } from '@/time/clock';
import { type Weather, hasPrecipitation, weatherFor } from '@/time/weather';
import { type Area, type AreaId, type AuthoredAreaId } from '@/world/area';
import { makeDungeonArea } from '@/dungeon/area';
import { WeaponGrowth } from '@/world/weaponGrowth';
import { WeaponAbilities } from '@/world/weaponAbilities';
import { Wallet } from '@/world/wallet';
import { Friendships } from '@/world/friendship';
import { RequestBoard } from '@/world/requests';
import { QuestLog } from '@/world/quests';
import { Festivals } from '@/world/festivals';
import { isBossDepth, makeArenaArea } from '@/dungeon/arena';
import { Farmland } from '@/world/farmland';
import { FurnitureLayer } from '@/world/furniture';
import { Inventory } from '@/world/inventory';
import { QuickBag } from '@/world/quickBag';
import { GroundItems } from '@/world/groundItems';
import { ToolBelt } from '@/world/toolbelt';
import { Livestock } from '@/world/livestock';
import { Player } from '@/world/player';
import { TILE_SIZE as TILE } from '@/sprites/tiles';

/**
 * The mutable state of a playthrough: where Katie is, when it is, and what the
 * sky is doing.
 *
 * Pulled out of main.ts so the rules — how a transition resolves, when weather
 * changes, what sleeping does — are testable without a renderer.
 */

export interface SessionOptions {
  readonly areaId?: AuthoredAreaId;
  readonly playerX?: number;
  readonly playerY?: number;
  readonly totalMinutes?: number;
  readonly seed: number;
  readonly farmland?: Farmland;
  readonly inventory?: Inventory;
  readonly quickBag?: QuickBag;
  readonly ground?: unknown;
  readonly weaponGrowth?: unknown;
  readonly weaponAbilities?: unknown;
  readonly coins?: unknown;
  readonly friendships?: unknown;
  readonly requests?: unknown;
  readonly quests?: unknown;
  readonly festivals?: unknown;
  /** Floors below the surface, for a save written underground. */
  readonly depth?: number;
  /** And where that descent started, so climbing out still works. */
  readonly surfaceReturn?: unknown;
  readonly belt?: unknown;
  readonly livestock?: unknown;
  readonly furniture?: Record<string, unknown>;
}

export interface TransitionEvent {
  readonly from: AreaId;
  readonly to: AreaId;
}

export class Session {
  readonly clock: GameClock;
  readonly seed: number;
  readonly farmland: Farmland;
  readonly inventory: Inventory;
  /** What is to hand, pointing into the backpack. */
  readonly quickBag: QuickBag;
  /** Items left lying about, per area. */
  readonly ground: GroundItems;
  /** How much each weapon has been swung, and so how good it has become. */
  readonly weaponGrowth: WeaponGrowth;
  /** What has been fed into each weapon. */
  readonly weaponAbilities: WeaponAbilities;
  /** Coins. */
  readonly wallet: Wallet;
  /** What everyone thinks of you. */
  readonly friendships: Friendships;
  /** What the neighbours are asking for, and what you have already handed in. */
  readonly requests: RequestBoard;
  /** How far through each quest. */
  readonly quests: QuestLog;
  /** Which festivals have been entered, and in which year. */
  readonly festivals: Festivals;
  readonly belt: ToolBelt;
  readonly livestock: Livestock | null;
  /** Furniture is per-area, so each room keeps its own arrangement. */
  private readonly furnitureByArea = new Map<AreaId, FurnitureLayer>();
  player: Player;
  area: Area;

  /** Day the farm was last rolled forward, so a jump of several days catches up. */
  private lastGrowthDay: number;

  private cachedWeatherDay = -1;
  private cachedWeather: Weather = 'clear';

  constructor(options: SessionOptions) {
    this.seed = options.seed;
    this.area = getArea(isAreaId(options.areaId) ? options.areaId : STARTING_AREA);

    // A save written underground names a depth rather than an area, because
    // 'dungeon' is not a place — the floor is rebuilt from (seed, depth), and
    // the same seed and depth is the same layout, so the coordinates that
    // came with it are still standing on floor.
    const depth = Math.floor(Number(options.depth ?? 0));
    if (Number.isFinite(depth) && depth > 0) {
      this.depth = depth;
      this.area = this.floorFor(depth);
      this.surfaceReturn = readSurfaceReturn(options.surfaceReturn);
    }

    const spawn = this.area.definition.spawn;
    this.player = new Player(options.playerX ?? spawn.x, options.playerY ?? spawn.y);

    this.clock = new GameClock(
      options.totalMinutes === undefined ? {} : { totalMinutes: options.totalMinutes },
    );
    this.farmland = options.farmland ?? new Farmland();
    this.inventory = options.inventory ?? Inventory.starting();
    this.quickBag = options.quickBag ?? QuickBag.starting();
    this.ground = GroundItems.fromSave(options.ground);
    this.weaponGrowth = WeaponGrowth.fromSave(options.weaponGrowth);
    this.weaponAbilities = WeaponAbilities.fromSave(options.weaponAbilities);
    this.wallet = Wallet.fromSave(options.coins);
    this.friendships = Friendships.fromSave(options.friendships);
    this.requests = RequestBoard.fromSave(this.seed, options.requests);
    this.quests = QuestLog.fromSave(options.quests);
    this.festivals = Festivals.fromSave(options.festivals);
    this.belt = options.belt === undefined ? ToolBelt.starting() : ToolBelt.fromSave(options.belt);

    // Animals belong to the farm, which is the only area with a pen.
    const pen = getArea(STARTING_AREA).definition.pen;
    this.livestock = pen ? Livestock.fromSave(options.livestock, pen) : null;

    for (const [id, entries] of Object.entries(options.furniture ?? {})) {
      if (isAreaId(id)) this.furnitureByArea.set(id, FurnitureLayer.fromSave(entries));
    }

    // Rooms a save has never touched start with whatever the map says is in
    // them; a saved room keeps exactly what the player left, empty or not.
    for (const id of Object.keys(AREA_DEFINITIONS) as AuthoredAreaId[]) {
      if (this.furnitureByArea.has(id)) continue;
      const initial = getArea(id).definition.furniture;
      if (initial?.length) this.furnitureByArea.set(id, FurnitureLayer.fromSave(initial));
    }

    this.lastGrowthDay = this.clock.dayIndex;

    // A restored position can land inside geometry if the map has been edited
    // since the save was written. Falling back to the spawn beats being stuck.
    if (this.area.map.collides(this.player.box)) {
      this.player = new Player(spawn.x, spawn.y);
    }
  }

  /**
   * Roll the farm forward for any days that have passed.
   *
   * Driven off the clock rather than called from the sleep handler, so time
   * moving for any reason — sleeping, a festival skip, a restored save from
   * days ago — grows the crops exactly once per day.
   */
  syncGrowth(): number {
    const today = this.clock.dayIndex;
    let advanced = 0;

    // Capped so a save restored after a long absence cannot stall the frame.
    const catchUp = Math.min(today - this.lastGrowthDay, 60);
    for (let i = 0; i < catchUp; i++) {
      const day = this.lastGrowthDay + i;
      // Rain counts as having watered that day, so a wet day is a day off.
      // Applied before growth because it is the day being left behind that
      // determines whether the crops drank.
      if (hasPrecipitation(weatherFor(this.seed, day, this.clock.season))) {
        this.farmland.waterAll(day);
      }
      this.farmland.advanceDay(day, this.clock.season);
      this.livestock?.advanceDay(day);
      advanced++;
    }

    this.lastGrowthDay = today;
    return advanced;
  }

  /**
   * What is currently on the hook.
   *
   * Held on the session rather than in the hotbar because bait is not "in
   * hand" — the rod is. Choosing bait is a tackle-box decision that persists
   * across casts.
   */
  equippedBait: string | null = 'bait:worm';

  /** Tile Katie is facing, which is what tools act on. */
  get facingTile(): { x: number; y: number } {
    const deltas = {
      up: [0, -1],
      down: [0, 1],
      left: [-1, 0],
      right: [1, 0],
    } as const;
    const [dx, dy] = deltas[this.player.facing];
    return { x: this.player.tileX + dx, y: this.player.tileY + dy };
  }

  /**
   * Move straight to an area, keeping the player where they are.
   * Used by the debug spawn parameters; ordinary travel goes through portals.
   */
  enterArea(areaId: AuthoredAreaId): void {
    this.area = getArea(areaId);
  }

  /**
   * How far underground, or 0 on the surface.
   *
   * Saved, because a floor is regenerated from (seed, depth) and so a save
   * that names the depth names the floor exactly. It did not used to be: a
   * reload underground put Katie at the farm spawn — outside her own front
   * door — and the run was simply gone. That is not a fair thing to do to
   * somebody six floors down who tapped reload to clear a stuck browser zoom.
   */
  depth = 0;

  /** Where the descent started, so climbing out puts you back. */
  private surfaceReturn: { area: AuthoredAreaId; x: number; y: number } | null = null;

  get underground(): boolean {
    return this.depth > 0;
  }

  /**
   * Go one floor down, remembering the way out on the first step.
   *
   * The floor is built rather than fetched: `getArea` caches forever, which is
   * right for the three authored places and wrong for something regenerated on
   * every descent.
   */
  descend(): void {
    if (this.depth === 0) {
      this.surfaceReturn = {
        area: this.area.id as AuthoredAreaId,
        x: this.player.x,
        y: this.player.y,
      };
    }

    this.depth += 1;
    this.area = this.floorFor(this.depth);
    this.player.teleport(this.area.definition.spawn.x, this.area.definition.spawn.y);
  }

  /**
   * Climb one floor. From the first floor this leaves the dungeon entirely,
   * putting Katie back exactly where she went in.
   */
  ascend(): void {
    if (this.depth <= 1) {
      const back = this.surfaceReturn;
      this.depth = 0;
      this.surfaceReturn = null;

      if (back) {
        this.area = getArea(back.area);
        this.player.teleport(back.x, back.y);
      } else {
        this.sendToSpawn(STARTING_AREA);
      }
      return;
    }

    this.depth -= 1;
    this.area = this.floorFor(this.depth);
    // Arriving from below stands you on the way down you just came up.
    const floor = this.area.definition;
    this.player.teleport(floor.spawn.x, floor.spawn.y);
  }

  /** Where climbing out leads, for the save. Null above ground. */
  surfaceReturnToSave(): { area: string; x: number; y: number } | null {
    return this.surfaceReturn;
  }

  /** Every tenth floor is a hand-shaped arena rather than a generated one. */
  private floorFor(depth: number): Area {
    return isBossDepth(depth) ? makeArenaArea(depth) : makeDungeonArea(this.seed, depth);
  }

  /**
   * Move to an area and stand at the spot it declares.
   *
   * `enterArea` deliberately leaves Katie where she is, which is what the
   * debug jump wants. Waking up somewhere is a different act, and it must
   * carry a position: a KO used to change the area and not the coordinates,
   * leaving her at the *coast's* position inside a fourteen-tile room —
   * off-screen, and wedged against out-of-bounds collision so she could not
   * move until the page was reloaded.
   */
  sendToSpawn(areaId: AuthoredAreaId): void {
    // Being moved to an authored place ends any descent. Dying underground
    // used to leave `depth` where it was, so Katie woke in her own bedroom
    // still counted as six floors down — with the dungeon's chests and spawn
    // rules following her home.
    this.depth = 0;
    this.surfaceReturn = null;

    this.area = getArea(areaId);
    const { spawn } = this.area.definition;
    this.player.teleport(spawn.x, spawn.y);
  }

  /** The furniture layer for an area, created on first use. */
  furnitureFor(areaId: AreaId): FurnitureLayer {
    let layer = this.furnitureByArea.get(areaId);
    if (!layer) {
      layer = new FurnitureLayer();
      this.furnitureByArea.set(areaId, layer);
    }
    return layer;
  }

  get furniture(): FurnitureLayer {
    return this.furnitureFor(this.area.id);
  }

  furnitureToSave(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [id, layer] of this.furnitureByArea) {
      if (layer.size > 0) out[id] = layer.toSave();
    }
    return out;
  }

  /** Animals only exist where there is a pen to keep them in. */
  /** The plot layer, when standing somewhere it belongs. */
  get farmlandHere(): Farmland | null {
    return this.area.definition.farmable ? this.farmland : null;
  }

  get livestockHere(): Livestock | null {
    return this.area.definition.pen ? this.livestock : null;
  }

  /** Today's weather. Derived from the seed, so it never needs saving. */
  get weather(): Weather {
    const day = this.clock.dayIndex;
    if (day !== this.cachedWeatherDay) {
      this.cachedWeatherDay = day;
      this.cachedWeather = weatherFor(this.seed, day, this.clock.season);
    }
    return this.cachedWeather;
  }

  /** Weather has no effect indoors, so the overlay must know where we are. */
  get indoor(): boolean {
    return this.area.indoor;
  }

  /**
   * Is the player standing on a door?
   *
   * Detection and the move itself are separate calls so the caller can fade the
   * screen out in between. Committing immediately would show the destination
   * for a frame before the fade covered it.
   */
  tryTransitionPending(): boolean {
    return this.area.portalAt(this.player.tileX, this.player.tileY) !== undefined;
  }

  /** Complete the move detected by tryTransitionPending. */
  commitTransition(): TransitionEvent | null {
    const portal = this.area.portalAt(this.player.tileX, this.player.tileY);
    if (!portal) return null;

    const from = this.area.id;
    // Facing carries through the door, so walking south into a house leaves
    // Katie facing south on the other side rather than snapping to a default.
    const facing = this.player.facing;

    this.area = getArea(portal.toArea);
    this.player = new Player(portal.toX * TILE, portal.toY * TILE);
    this.player.facing = facing;

    return { from, to: this.area.id };
  }

  /** Is the player standing next to this area's bed? */
  canSleep(): boolean {
    const bed = this.area.definition.bed;
    if (!bed) return false;
    const dx = Math.abs(this.player.tileX - bed.x);
    const dy = Math.abs(this.player.tileY - bed.y);
    // Orthogonally or diagonally adjacent, and the tile itself counts as close
    // enough because the bed occupies more than one tile visually.
    return dx <= 1 && dy <= 1;
  }

  /** Sleep through to 6am. Returns false if there is no bed to hand. */
  sleep(): boolean {
    if (!this.canSleep()) return false;
    this.clock.sleepUntilMorning();
    return true;
  }
}

/**
 * The way back up, off a save.
 *
 * A descent with no remembered surface is recoverable — `ascend` falls back to
 * the farm — so anything unreadable becomes null rather than throwing away the
 * rest of the save.
 */
function readSurfaceReturn(value: unknown): { area: AuthoredAreaId; x: number; y: number } | null {
  if (!value || typeof value !== 'object') return null;

  const raw = value as { area?: unknown; x?: unknown; y?: unknown };
  if (!isAreaId(raw.area)) return null;
  if (!Number.isFinite(Number(raw.x)) || !Number.isFinite(Number(raw.y))) return null;

  return { area: raw.area, x: Number(raw.x), y: Number(raw.y) };
}
